import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CircleCheck, CircleHelp, X } from 'lucide-react';
import { Toaster, toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { displayTime, parisDate } from './shared';
import { GeoPermissionGuide } from './GeoPermissionGuide';

const outcome = event => {
  if (event.stage === 'settings_error') return { obtained: false, label: 'Activation non vérifiable.' };
  if (event.stage === 'send_error') return { obtained: false, label: 'Enregistrement du résultat de localisation non confirmé par le serveur.' };
  const status = event.server_status;
  if (['capturee', 'imprecise'].includes(status)) return { obtained: true, label: status === 'imprecise' ? 'Précision limitée.' : '' };
  if (status === 'permission_refusee') return { obtained: false, label: 'Accès refusé par le navigateur ou le système.' };
  if (status === 'non_relevee') return { obtained: false, label: 'Vous avez choisi de ne pas relever votre position.' };
  return { obtained: false, label: 'Position indisponible : problème technique ou délai dépassé.' };
};

const GeoOutcome = ({ event, onHelp, onDismiss }) => {
  const result = outcome(event);
  return <div className="w-full max-w-[calc(100vw-2rem)] rounded-md border border-slate-200 bg-white p-4 shadow-lg text-slate-900" role="status" data-testid="geo-capture-feedback">
    <div className="flex items-start gap-2">
      {result.obtained && <CircleCheck className="w-5 h-5 shrink-0 text-emerald-700 mt-0.5" />}
      <p className="font-semibold text-sm min-w-0 flex-1" data-testid="geo-capture-feedback-message">Pointage enregistré — position {result.obtained ? 'obtenue' : 'non obtenue'}</p>
      <button type="button" className="shrink-0 p-1 rounded hover:bg-slate-100" aria-label="Fermer le résultat du pointage" title="Fermer" onClick={onDismiss} data-testid="geo-result-dismiss"><X className="w-4 h-4" /></button>
    </div>
    <p className="text-xs text-slate-600 mt-2" data-testid="geo-result-category">{event.phase === 'start' ? 'Début' : 'Fin'}{result.label ? ` · ${result.label}` : ''}</p>
    {!result.obtained && <Button variant="outline" className="w-full h-auto min-h-11 mt-3 py-2 whitespace-normal text-left" onClick={onHelp} data-testid="geo-result-help"><CircleHelp className="w-4 h-4 mr-2 shrink-0" />Aide pour autoriser ma position</Button>}
    <Link className="inline-block text-xs underline mt-3" onClick={onDismiss} to={`/internal/hours?entry=${encodeURIComponent(event.entry_id)}&month=${parisDate(new Date(event.pointing_at)).slice(0, 7)}`} data-testid="geo-capture-feedback-detail">Voir le détail</Link>
  </div>;
};

export const GeoCaptureFeedback = ({ event }) => {
  const [guideOpen, setGuideOpen] = useState(false);
  const handled = useRef(null);
  const activeToast = useRef(null);
  useEffect(() => {
    if (!event) return;
    if (event.stage === 'settings') {
      if (activeToast.current) toast.dismiss(activeToast.current);
      activeToast.current = null; return;
    }
    if (event.enabled !== true || !['saved', 'settings_error', 'send_error'].includes(event.stage)) return;
    const key = `${event.entry_id}:${event.phase}:${event.at}:${event.stage}`;
    if (handled.current === key) return;
    handled.current = key;
    const id = `dsp-geo-result:${key}`;
    activeToast.current = id;
    const dismiss = () => { toast.dismiss(id); if (activeToast.current === id) activeToast.current = null; };
    toast.custom(() => <GeoOutcome event={event} onHelp={() => { setGuideOpen(true); dismiss(); }} onDismiss={dismiss} />,
      { id, duration: 9000, toasterId: 'dsp-geo' });
  }, [event]);
  useEffect(() => () => { if (activeToast.current) toast.dismiss(activeToast.current); }, []);
  return <>
    <Toaster id="dsp-geo" position="bottom-center" offset={80} mobileOffset={{ bottom: 80, left: 16, right: 16 }} visibleToasts={1} />
    <GeoPermissionGuide open={guideOpen} onOpenChange={setGuideOpen} idPrefix="feedback-geo-help" />
  </>;
};

export const GeoCaptureDiagnostic = ({ entry, journal }) => {
  const events = ['start', 'end'].map(phase => journal[`${entry.entry_id}:${phase}`]).filter(e => e && e.pointing_at === (e.phase === 'start' ? entry.started_at : entry.ended_at));
  if (!events.length) return null;
  return <section className="border-t py-4 space-y-3" data-testid="geo-capture-diagnostic">
    <h2 className="text-base font-semibold">Diagnostic de cette session</h2>
    {events.map(e => <div className="text-sm space-y-1" key={e.phase} data-testid={`geo-diagnostic-${e.phase}`}>
      <p className="font-medium" data-testid={`geo-diagnostic-${e.phase}-time`}>{e.phase === 'start' ? 'Début' : 'Fin'} · {displayTime(e.pointing_at)}</p>
      <p data-testid={`geo-diagnostic-${e.phase}-message`}>{e.message}</p>
      <p className="text-xs text-slate-600 break-words" data-testid={`geo-diagnostic-${e.phase}-stages`}>Étape : {e.stage}{typeof e.enabled === 'boolean' ? ` · Activation : ${e.enabled ? 'oui' : 'non'}` : ''}{e.browser_status ? ` · Résultat : ${e.browser_status}` : ''}{e.browser_code ? ` · Code navigateur : ${e.browser_code}` : ''}{e.http_status ? ` · Serveur : HTTP ${e.http_status}` : ''}</p>
    </div>)}
  </section>;
};